import { Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { ButtonComponent } from "@controls/button/button.component";
import { Alert } from "@models/alerts/alert";
import { AlertRecord } from "@models/alerts/alert-record";
import { AlertStatus, AlertType } from "@models/alerts/alerts-enums";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import {
  AlertDetailsContentComponent
} from "@pages/alerts/components/alert-details-content/alert-details-content.component";
import {
  OffcanvasPaginationTitleComponent
} from "@shared/components/commons/common-offcanvas/components/offcanvas-pagination-title/offcanvas-pagination-title.component";
import {
  AlertsPageConfigKeyEnum,
  getAlertPageConfig,
  IAlertPageConfig
} from "@pages/alerts/components/alerts-page/alerts-page-configs";
import { postponeMenu } from "@pages/alerts/constants/postpone-options";
import { AlertsExportService } from "@pages/alerts/services/alerts-export.service";
import { AlertsGridService } from "@pages/alerts/services/alerts-grid.service";
import { CommonOffcanvasComponent } from "@shared/components/commons/common-offcanvas/common-offcanvas.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import {
  AgAlertsActionsComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-actions/ag-alerts-actions.component";
import {
  GridServerSideComponent
} from "@shared/components/grid/components/grid-server-side/grid-server-side.component";
import { AlertsTypePipe } from "@shared/pipes/alertsTypePipe";
import { AlertsService } from "@shared/services/alert.service";
import { AlertsApiService } from "@shared/services/alerts-api.service";
import { MenuService } from "@shared/services/menu.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { ColDef, ExcelExportParams, GetRowIdFunc, GetRowIdParams, GridApi } from "ag-grid-community";
import { observe, Observer } from "fast-json-patch";
import { ToastrService } from "ngx-toastr";
import { debounceTime, filter, Subject, take } from "rxjs";

@Component({
  selector: 'app-alerts-page',
  standalone: true,
  imports: [
    AlertsTypePipe,
    ButtonComponent,
    GridServerSideComponent,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    AlertDetailsContentComponent,
    CommonOffcanvasComponent,
    OffcanvasPaginationTitleComponent
  ],
  providers: [
    AlertsGridService,
    AlertsExportService
  ],
  templateUrl: 'alerts-page.component.html',
})
export class AlertsPageComponent extends CommonPageComponent implements OnInit {
  alertsApiService = inject(AlertsApiService);
  alertsGridService = inject(AlertsGridService);
  alertsExportService = inject(AlertsExportService);
  organisationService = inject(OrganisationApiService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  alertsService = inject(AlertsService);
  menuService = inject(MenuService);
  toastr = inject(ToastrService);
  destroyRef: DestroyRef = inject(DestroyRef);

  readonly ExportTypeEnum = ExportTypeEnum;
  readonly AlertType = AlertType;
  readonly postponeMenu = postponeMenu;
  readonly alertTypesAllowedForIgnoredOrPostponed = [AlertType.XpmSyncIssue, AlertType.IdentifiedExternalChanges, AlertType.AsicDataIssue];

  pageConfig!: IAlertPageConfig;
  observer!: Observer<Alert[]>;
  currentAlertIndex = 0;
  checkedInboxAlerts: AlertRecord[] = [];
  alertRows: AlertRecord[] = [];
  isPostponeAllowed = false;
  isMarkAsNeedAttentionAllowed = false;

  $showDetails: WritableSignal<boolean> = signal(false);
  $selectedAlert = signal<Alert | null>(null);

  alertRowsLoaded$ = new Subject<boolean>();

  override ngOnInit(): void {
    super.ngOnInit();
    this.listenAlertRowsLoaded();
    this.menuService.setMenuItemOpenState('Alerts');

    this.route.data
      .pipe(take(1))
      .subscribe((data) => {
        this.pageConfig = getAlertPageConfig(data['key']);
        this.headerService.setHeaderData(this.pageConfig.headerData);

        this.setExcelExportParams();
        this.setGridConfig();
        this.setGridFilterOptions();
        this.setIsPostponeAllowed();
        this.setIsMarkAsNeedAttentionAllowed();
      });
  }

  listenAlertRowsLoaded(): void {
    this.alertsGridService.dataLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((alertRows) => this.alertRows = alertRows.records);

    this.alertsGridService.dataLoaded$
      .pipe(take(1))
      .subscribe(() => this.listenAlertDetailsQueryParams());
  }

  listenAlertDetailsQueryParams(): void {
    this.route.queryParams
      .pipe(
        filter(params => !!params['alertId'] && !this.$showDetails()),
        debounceTime(1000),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((params) => {
        const findIndex = this.alertRows.findIndex(row => row.id === params['alertId']);

        if (findIndex < 0) return;

        this.openAlert({ alertRecord: this.alertRows[findIndex], index: findIndex });
      });
  }

  removeQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  bulkMarkRead(): void {
    const unreadAlerts = this.checkedInboxAlerts.filter(alert => !alert.isRead);

    if (unreadAlerts.length) {
      const alertIds = unreadAlerts.map(alert => alert.id);
      this.observer = observe(unreadAlerts[0]);
      unreadAlerts[0].isRead = true;
      this.bulkUpdateAlerts(alertIds, this.observer);
      this.toastr.success('Alert(s) marked as read', 'Success');
    }
  }

  bulkMarkRequireAttention() {
    if (!this.isMarkAsNeedAttentionAllowed) return;

    const notNeedAttentionAlerts = this.checkedInboxAlerts.filter(alert => !alert.needAttention);

    if (notNeedAttentionAlerts.length) {
      const alertIds = notNeedAttentionAlerts.map(alert => alert.id);
      this.observer = observe(notNeedAttentionAlerts[0]);
      notNeedAttentionAlerts[0].needAttention = true;
      this.bulkUpdateAlerts(alertIds, this.observer);
    }
  }

  bulkArchive(): void {
    const notArchivedAlerts = this.checkedInboxAlerts.filter(alert => alert.status !== AlertStatus.Archived);
    if (notArchivedAlerts.length) {
      const alertIds = notArchivedAlerts.map(alert => alert.id);
      this.observer = observe(notArchivedAlerts[0]);
      notArchivedAlerts[0].status = AlertStatus.Archived;

      this.bulkUpdateAlerts(alertIds, this.observer);
    }
  }

  bulkIgnore(): void {
    const notIgnoredAlerts = this.checkedInboxAlerts
      .filter((alert) => {
        return alert.status !== AlertStatus.Ignored
          && this.alertTypesAllowedForIgnoredOrPostponed.some((type) => type === alert.type);
      });

    if (notIgnoredAlerts.length) {
      const alertIds = notIgnoredAlerts.map(alert => alert.id);
      this.observer = observe(notIgnoredAlerts[0]);
      notIgnoredAlerts[0].status = AlertStatus.Ignored;
      this.bulkUpdateAlerts(alertIds, this.observer);
    }
  }

  bulkPostpone(numberOfDays: number): void {
    if (!this.isPostponeAllowed) return;

    const notPostponedAlerts = this.checkedInboxAlerts
      .filter(alert => {
        return alert.status !== AlertStatus.Postponed
          && this.alertTypesAllowedForIgnoredOrPostponed.some(type => type === alert.type);
      });

    if (notPostponedAlerts.length) {
      const alertIds = notPostponedAlerts.map(alert => alert.id);
      this.observer = observe(notPostponedAlerts[0]);
      notPostponedAlerts[0].status = AlertStatus.Postponed;

      const currentDate = new Date();
      const postponedDate = new Date();
      postponedDate.setDate(currentDate.getDate() + numberOfDays);
      notPostponedAlerts[0].postponedTill = postponedDate;

      this.bulkUpdateAlerts(alertIds, this.observer);
    }
  }

  bulkUpdateAlerts(alertIds: string[], observer: Observer<Alert[]>): void {
    this.alertsApiService.bulkUpdateAlert(alertIds, observer)
      .subscribe((alerts) => {
        if (this.router.url === '/alerts/inbox') {
          this.gridApi.refreshServerSide();
        } else {
          if (alerts.length) {
            alerts.forEach((alert) => {
              const rowNode = this.gridApi.getRowNode(alert.id);
              rowNode?.setData(Alert.toAlertRecord(alert));
              rowNode?.setSelected(false);
              this.gridApi.refreshServerSide({ route: [alert.id] });
            });
          }
        }
      });
  }

  markAllRead(): void {
    const unreadAlerts = this.alertRows.filter(alert => !alert.isRead);

    if (unreadAlerts.length) {
      const alertIds = unreadAlerts.map(alert => alert.id);
      this.observer = observe(unreadAlerts[0]);
      unreadAlerts[0].isRead = true;

      this.alertsApiService.bulkUpdateAlert(alertIds, this.observer)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((alerts) => {
          if (alerts.length) {
            this.gridApi.setGridOption("serverSideDatasource", this.dataSource());
          }
          this.toastr.success('Alert(s) marked as read', 'Success');
        });
    } else {
      this.toastr.success('All alerts have already been read', 'Success');
    }
  }

  openAlert(data: { alertRecord: AlertRecord, index: number }) {
    this.alertsApiService.getAlert(data.alertRecord.id)
      .subscribe({
        next: (alert) => {
          this.$selectedAlert.set(alert);
          this.$showDetails.set(true);
          this.currentAlertIndex = data.index;
        },
        error: () => {
          this.toastr.error('Error happen', 'Error');
        },
        complete: () => {
          this.gridApi.refreshServerSide({ route: [data.alertRecord.id] });
        }
      });
  }

  closeDetails(): void {
    this.gridApi.refreshServerSide();
    this.$showDetails.set(false);
    this.$selectedAlert.set(null);
    this.removeQueryParams();
  }

  previousAlert(): void {
    if (this.currentAlertIndex > 0) {
      this.currentAlertIndex--;
      const rowsNumber = this.gridApi.paginationGetPageSize() * this.gridApi.paginationGetCurrentPage() - 1;

      if (this.currentAlertIndex === rowsNumber) {
        this.gridApi.paginationGoToPreviousPage();
        this.updateSelectedAlert(true);
      } else {
        this.updateSelectedAlert(false);
      }
    }
  }

  nextAlert(): void {
    if (this.currentAlertIndex < this.totalRows()) {
      this.currentAlertIndex++;
      const rowsNumber = (this.gridApi.paginationGetPageSize() * (this.gridApi.paginationGetCurrentPage() + 1));

      if (this.currentAlertIndex === rowsNumber) {
        this.gridApi.paginationGoToNextPage();
        this.updateSelectedAlert(true);
      } else {
        this.updateSelectedAlert(false);
      }
    }
  }

  updateAlertGridRow(alert: Alert): void {
    const updatedNode = this.gridApi.getRowNode(alert.id);
    updatedNode?.setData(Alert.toAlertRecord(alert));

    if (updatedNode) {
      this.gridApi.refreshCells({
        rowNodes: [updatedNode]
      });
    }
  }

  updateSelectedAlert(changedPage: boolean): void {
    let currentIndex = 0;

    if (this.currentAlertIndex < this.gridApi.paginationGetPageSize()) {
      currentIndex = this.currentAlertIndex;
    } else {
      const countRows = (this.gridApi.paginationGetPageSize() * this.gridApi.paginationGetCurrentPage());

      if (countRows) {
        currentIndex = this.currentAlertIndex % (this.gridApi.paginationGetPageSize() * this.gridApi.paginationGetCurrentPage());
      } else {
        currentIndex = 0;
      }
    }

    if (changedPage) {
      this.alertRowsLoaded$
        .pipe(take(1))
        .subscribe(() => {
          this.fetchAlerts(this.alertRows[currentIndex].id);
        });
    } else {
      const id = this.$selectedAlert()?.id ?? '';

      this.alertRows.forEach((row) => {
        if (row.id === id) {
          this.fetchAlerts(this.alertRows[currentIndex].id);
        }
      });
    }
  }

  fetchAlerts(alertId: string): void {
    this.alertsApiService.getAlert(alertId).subscribe({
      next: (alert: Alert | null) => {
        this.$selectedAlert.set(alert);
      },
      error: () => {
        this.toastr.error('Error happen', 'Error');
      }
    });
  }

  setIsPostponeAllowed(): void {
    const allowedPostponePagesKeys = [
      AlertsPageConfigKeyEnum.All,
      AlertsPageConfigKeyEnum.Inbox
    ];

    this.isPostponeAllowed = allowedPostponePagesKeys.includes(this.pageConfig.key);
  }

  setIsMarkAsNeedAttentionAllowed(): void {
    const allowedMarkAsNeedAttentionPagesKeys = [
      AlertsPageConfigKeyEnum.All,
      AlertsPageConfigKeyEnum.Inbox,
      AlertsPageConfigKeyEnum.Postponed,
    ];

    this.isMarkAsNeedAttentionAllowed = allowedMarkAsNeedAttentionPagesKeys.includes(this.pageConfig.key);
  }

  // =====  Grid  =====

  readonly defaultPageSize = PageSizeService.defaultPageSize;
  readonly cacheBlockSize = 20;
  readonly maxBlocksInCache = 1;
  readonly gridOptions = {
    overlayNoRowsTemplate: '<span class="no-rows-overlay">No data available</span>',
  };
  readonly getRowId: GetRowIdFunc = (params: GetRowIdParams<AlertRecord>) => params.data.id;

  gridApi!: GridApi;
  totalRows = this.alertsGridService.totalRecords;
  colDefs = this.alertsGridService.colDefs;
  dataSource = this.alertsGridService.dataSource;

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
    this.gridApi?.addEventListener('modelUpdated', () => {
      const totalPages = this.gridApi.paginationGetTotalPages();
      if (!totalPages) {
        this.gridApi.showNoRowsOverlay();
      } else {
        this.gridApi.hideOverlay();
      }
    });
  }

  selectInboxAlerts(alerts: AlertRecord[]): void {
    this.checkedInboxAlerts = alerts;
  }

  onSearch(searchText: string): void {
    this.alertsGridService.setDataSource(searchText);
  }

  setGridFilterOptions(): void {
    this.alertsGridService.initMapRequestFn(this.pageConfig.mapRequestFn);
    this.alertsGridService.setGridFilterOptions(this.organisationService.getCachedProfileUsers().map(u => u?.fullName));
  }

  setGridConfig(): void {
    const actionColumn: ColDef = {
      width: 90,
      sortable: false,
      cellRenderer: AgAlertsActionsComponent,
      cellRendererParams: {
        openAlert: this.openAlert.bind(this),
      },
      menuTabs: []
    };

    this.alertsGridService.setColDefsWithKey(
      actionColumn,
      this.pageConfig.key,
      this.openAlert.bind(this)
    );
  }

  // =====  Export  =====

  excelExportParams!: ExcelExportParams;
  excelStyles = this.alertsExportService.excelStyles;

  setExcelExportParams(): void {
    this.excelExportParams = this.alertsExportService.exportParamsXls(this.alertsService.numberColumnForExport(), this.pageConfig.sheetName);
  }

  exportSelectedAlertsToXls(): void {
    this.exportAlertsList(true, ExportTypeEnum.EXCEL);
  }

  exportAlertsList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    this.alertsExportService.exportAlertsList(
      this.gridApi,
      exportType,
      isBulkExport,
      this.colDefs(),
      this.checkedInboxAlerts,
      this.totalRows(),
      this.pageConfig.sheetName
    );
  }
}
